import React, { useEffect, useState } from 'react';
import MuiTable from '../../components/MuiTable/MuiTable';
import PageHeading from '../../components/PageHeading/PageHeading';
import {
  updateDownloadID,
  updateFlashBar,
  updateIsLoading,
  update_breadcrumb,
} from '../../redux/action';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import instance from '../../service/axiosInstane';
import { flashBarTheme } from '../../config/flashBar';
import SearchInput from '../../components/SearchInput/SearchInput';
import { SortOrderType } from '../../config/enum';
import qs from 'qs';
import { totalPages } from '../../config/helpers';
import { StateProps } from '../../redux/reducer';

const columns = [
  {
    id: 'host',
    label: 'Host',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'name',
    label: 'Name',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'createdBy',
    label: 'Created by',
    minWidth: 150,
    align: 'left',
  },
  {
    id: 'createdDate',
    label: 'Created date',
    minWidth: 150,
    align: 'center',
  },
  {
    id: 'src-download',
    label: 'Source File',
    minWidth: 150,
    align: 'center',
  },
  {
    id: 'action',
    label: 'Action',
    minWidth: 0,
    align: 'right',
  },
];

type ResultProps = {
  id: string;
  dmcaFilesId: string;
  name: string;
  siteUrl: string;
  host: string;
  createdDate: string;
  documentStoreId: string;
  updatedDate: string;
  sequenceNumber: number;
};

type DmcaFileResponceProps = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  results?: ResultProps[];
};

type RequestProps = {
  page: number;
  pageSize: number;
  searchText: string;
  sortBy: string;
  sortOrder: string;
  organizationFilter: string[];
};

const DMCAFiles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state: StateProps) => state.tokenInfo.userInfo);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [dmcaFiles, setDmcaFiles] = useState<ResultProps[]>([]);
  const [requestState, setRequestState] = useState<RequestProps>({
    page: page,
    pageSize: limit,
    searchText: '',
    sortBy: '',
    sortOrder: SortOrderType.ASC,
    organizationFilter: [userInfo.organizationId],
  });

  useEffect(() => {
    const breadcrumb = [{ path: '/dmcaFiles', name: 'DMCA Files' }];
    dispatch(update_breadcrumb(breadcrumb));
    getAllDmcaFiles(requestState);
  }, []);

  const viewRecord = (row: ResultProps) => {
    localStorage.removeItem('isFrom');
    navigate(`/dmcaFilesView/${row.id}/${userInfo.id}`);
  };

  //Search
  const onSearchHanlder = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setRequestState({ ...requestState, searchText: e.target.value });
  };

  const searchHandler = () => {
    getAllDmcaFiles({
      ...requestState,
      searchText: requestState.searchText,
      pageSize: limit,
    });
  };

  const onClearSearch = () => {
    setRequestState({
      ...requestState,
      searchText: '',
      page: page,
      pageSize: limit,
    });
    getAllDmcaFiles({
      ...requestState,
      searchText: '',
      page: page,
      pageSize: limit,
    });
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchHandler();
    }
  };

  //Getting all DMCS Files
  const getAllDmcaFiles = async (obj: RequestProps) => {
    dispatch(updateIsLoading(true));
    const queryString = qs.stringify(obj, { arrayFormat: 'repeat' });
    await instance
      .get(`/dmca-files?${queryString}`)
      .then((response) => {
        if (response.status === 200) {
          gotResponse(response.data);
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const gotResponse = (data: DmcaFileResponceProps) => {
    setDmcaFiles(data?.results as ResultProps[]);
    setTotalRowCount(data.totalCount);
    dispatch(updateIsLoading(false));
  };

  //Pagination
  const paginateHandler = {
    onClickPageNumber: (page: number) => {
      getAllDmcaFiles({ ...requestState, page: page, pageSize: limit });
      setPage(page);
    },
    onNextClick: () => {
      if (page < totalPages(totalRowCount, limit)) {
        getAllDmcaFiles({
          ...requestState,
          page: page + 1,
          pageSize: limit,
          organizationFilter: requestState.organizationFilter,
        });
        setPage(page + 1);
      }
    },
    onPrevClick: () => {
      if (page > 1) {
        getAllDmcaFiles({
          ...requestState,
          page: page - 1,
          pageSize: limit,
          organizationFilter: requestState.organizationFilter,
        });
        setPage(page - 1);
      }
    },
    onFirstClick: () => {
      if (page !== 1) {
        getAllDmcaFiles({
          ...requestState,
          page: 1,
          pageSize: limit,
          organizationFilter: requestState.organizationFilter,
        });
        setPage(1);
      }
    },
    onLastClick: () => {
      if (page !== totalPages(totalRowCount, limit)) {
        getAllDmcaFiles({
          ...requestState,
          page: totalPages(totalRowCount, limit),
          pageSize: limit,
          organizationFilter: requestState.organizationFilter,
        });
        setPage(totalPages(totalRowCount, limit));
      }
    },

    limitHandler: (item: number) => {
      getAllDmcaFiles({
        ...requestState,
        page: 1,
        pageSize: item,
        organizationFilter: requestState.organizationFilter,
      });
      setLimit(item);
      setPage(1);
    },
  };

  //Sorting
  const sortRecord = (fieldName: string, sortOrder: string) => {
    getAllDmcaFiles({
      ...requestState,
      sortOrder: sorting(sortOrder),
      sortBy: fieldName,
      page: page,
      pageSize: limit,
    });
    setRequestState({
      ...requestState,
      sortBy: fieldName,
      sortOrder: sorting(sortOrder),
    });
  };

  const sorting = (sortOrder: string) => {
    let temp = '';
    if (sortOrder == SortOrderType.DESC) {
      temp = SortOrderType.ASC;
    } else {
      temp = SortOrderType.DESC;
    }
    setRequestState({ ...requestState, sortOrder: temp });
    return temp;
  };

  const srcFileDownload = (row: ResultProps) => {
    const obj = {
      isShow: true,
      downloadGuid: row.documentStoreId,
    };
    dispatch(updateDownloadID(obj));
  };

  return (
    <>
      <div className="px-2 w-full">
        <div className="flex items-center justify-between">
          <div className="w-full">
            <PageHeading label="DMCA Files Management" />
          </div>
        </div>
        <div className="bg-white p-2 rounded-sm">
          <div className="sm:w-full lg:flex items-center justify-between mb-2">
            <SearchInput
              value={requestState.searchText}
              onChange={onSearchHanlder}
              onSearch={searchHandler}
              onClear={onClearSearch}
              handleEnterKeyPress={handleEnterKeyPress}
            />
          </div>
          <div>
            <MuiTable
              columns={columns}
              rows={dmcaFiles}
              showView={true}
              onViewHandler={viewRecord}
              page={page}
              limit={limit}
              paginateHandler={paginateHandler}
              totalRowCount={totalRowCount}
              onSortHandler={sortRecord}
              sortOrder={requestState.sortOrder}
              sortArray={['createdDate']}
              onSrcFileDownload={srcFileDownload}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DMCAFiles;
